import type { Vec3 } from './types';

export const MAX_FILE_BYTES = 16 * 1024 * 1024;
export const MAX_BLOCKS = 100_000;
export const MAX_DIMENSION = 512;
/** 空気も含めた全セルを走査・確保するので、掛け合わせたセル数にも上限を設ける。 */
export const MAX_VOLUME = 2_000_000;

/**
 * パーサが出す、そのまま画面に出してよい日本語のエラー。
 * 文面での見分けはできないので、この型かどうかで「見せてよい説明」を判断する。
 */
export class StructureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StructureError';
  }
}

/**
 * ブロックIDに使われる文字だけを通す。`__proto__`のような名前が辞書を引く側へ
 * 流れ込まないよう、読み込みの時点で弾く。
 */
const BLOCK_NAME = /^[a-z0-9_.:/-]+$/;

export function checkBlockName(name: unknown, field: string): string {
  if (typeof name !== 'string' || name.length === 0) {
    throw new StructureError(`${field}がありません。`);
  }
  if (!BLOCK_NAME.test(name)) {
    throw new StructureError(`${field}がブロックIDの形ではありません。`);
  }
  return name;
}

export type UnknownRecord = Record<string, unknown>;

export function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asVec3(value: unknown, field: string): Vec3 {
  if (!Array.isArray(value) || value.length !== 3 || value.some((item) => !Number.isInteger(item))) {
    throw new StructureError(`${field}は整数3個の配列である必要があります。`);
  }
  return [Number(value[0]), Number(value[1]), Number(value[2])];
}

export function checkSize(size: Vec3) {
  if (size.some((value) => value <= 0 || value > MAX_DIMENSION)) {
    throw new StructureError(`sizeは1〜${MAX_DIMENSION}の範囲である必要があります。`);
  }
  // 各軸が上限内でも、掛け合わせたセル数は512³=1.3億まで伸びる。描画側はこのセル数で
  // 配列を確保するので、ブロックが1個しか無いファイルでもここで止める。
  if (size[0] * size[1] * size[2] > MAX_VOLUME) {
    throw new StructureError(`構造が大きすぎます。${MAX_VOLUME.toLocaleString()}マス以下にしてください。`);
  }
}
